import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format, startOfWeek, addDays } from 'date-fns';
import { es } from 'date-fns/locale';

// Determina si una reserva ya pasó (fecha anterior a hoy, o mismo día pero
// la hora de inicio del horario ya pasó). Misma lógica usada en el resto de la app.
const isReservationPast = (reservation) => {
  if (!reservation?.date) return false;
  const [year, month, day] = reservation.date.split('-').map(Number);
  const resDate = new Date(year, month - 1, day);
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (resDate < todayStart) return true;

  if (resDate.getTime() === todayStart.getTime()) {
    const startStr = (reservation.time_slot || '').split(' - ')[0]?.trim();
    if (!startStr) return false;
    const [hh, mm] = startStr.split(':').map(Number);
    if (Number.isNaN(hh) || Number.isNaN(mm)) return false;
    const resDateTime = new Date(year, month - 1, day, hh, mm);
    return resDateTime < now;
  }

  return false;
};

// Columna "Asistencia" en los PDFs: si la fecha aún no pasa, se muestra
// "Pendiente" en vez de Asistió/No asistió (todavía no se puede saber).
const getAsistenciaLabel = (r) => {
  if (!isReservationPast(r)) return 'Pendiente';
  return r.asistio === false ? 'No asistió' : 'Asistió';
};

const getFormattedDateTime = () => {
  const now = new Date();
  return format(now, 'dd/MM/yyyy HH:mm', { locale: es });
};

const getMonthName = (monthNum) => {
  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  return months[monthNum - 1] || '';
};

const getCurrentYear = () => {
  return new Date().getFullYear();
};

// ✅ FUNCIÓN INTELIGENTE: Detecta si es para usuario o admin
export const generateReservationsPDF = (reservations, userData, selectedMonths = [], selectedYear = null, selectedLevel = null) => {
  if (!reservations || reservations.length === 0) {
    console.warn('No hay reservas para generar PDF');
    return;
  }

  // 🔒 Filtro de seguridad: aunque el llamador ya debería enviar solo las
  // reservas del año y los meses elegidos, aquí se vuelve a filtrar por
  // "selectedYear" + "selectedMonths" para garantizar que el PDF JAMÁS
  // incluya un mes/año fuera de lo seleccionado (por ejemplo, un "Marzo" de
  // otro año distinto al elegido).
  const selectedMonthsSet = new Set((selectedMonths || []).map(Number));
  const reservationsToRender = selectedMonthsSet.size > 0
    ? reservations.filter((r) => {
        if (!r?.date) return false;
        const [y, m] = r.date.split('-').map(Number);
        if (!m) return false;
        if (selectedYear && Number(selectedYear) !== y) return false;
        return selectedMonthsSet.has(m);
      })
    : reservations;

  if (reservationsToRender.length === 0) {
    console.warn('No hay reservas dentro del año/meses seleccionados para generar PDF');
    return;
  }

  reservations = reservationsToRender;

  // 🎓 Filtro por nivel (Inicial / Primaria / Secundaria). Si no se indica
  // nivel, el comportamiento es el de siempre (todas las reservas recibidas).
  if (selectedLevel) {
    const lvl = String(selectedLevel).trim().toLowerCase();
    reservations = reservations.filter((r) => (r.nivel || '').trim().toLowerCase() === lvl);
    if (reservations.length === 0) {
      console.warn('No hay reservas del nivel seleccionado para generar PDF');
      return;
    }
  }

  const doc = new jsPDF('l', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const logoUrl = '/logo_aip.png';
  const dateTimeStr = `Generado el: ${getFormattedDateTime()}`;
  const currentYear = getCurrentYear();

  // ✅ 1. LÓGICA DE SUBTÍTULOS CORREGIDA (Bimestral, Trimestral, etc.)
  let subtitle = 'Informe de Reservas';
  const monthCount = selectedMonths.length;
  const monthNames = selectedMonths.map(m => getMonthName(m)).join(' - ');
  const yearSuffix = selectedYear ? ` ${selectedYear}` : '';

  if (monthCount === 1) {
    subtitle = `Informe Mensual: ${monthNames}${yearSuffix}`;
  } else if (monthCount === 2) {
    subtitle = `Informe Bimestral: ${monthNames}${yearSuffix}`;
  } else if (monthCount === 3) {
    subtitle = `Informe Trimestral: ${monthNames}${yearSuffix}`;
  } else if (monthCount === 6) {
    subtitle = `Informe Semestral: ${monthNames}${yearSuffix}`;
  } else if (monthCount === 12) {
    subtitle = `Informe Anual${yearSuffix}`;
  } else if (monthCount > 1) {
    subtitle = `Informe: ${monthNames}${yearSuffix}`;
  }

  const isForAdmin = !userData || !userData.nombres;

  if (isForAdmin) {
    // ✅ MODO ADMIN: Agrupado por Niveles
    const allEducationalLevels = ['Inicial', 'Primaria', 'Secundaria'];
    const educationalLevels = selectedLevel
      ? allEducationalLevels.filter((l) => l.toLowerCase() === String(selectedLevel).trim().toLowerCase())
      : allEducationalLevels;
    const filteredReservations = reservations.filter(r => educationalLevels.includes(r.nivel));

    if (filteredReservations.length === 0) return;

    let firstPage = true;
    educationalLevels.forEach(level => {
      const levelReservations = filteredReservations.filter(r => r.nivel === level);
      if (levelReservations.length === 0) return;

      if (!firstPage) doc.addPage();
      firstPage = false;

      // Encabezado Admin
      try { doc.addImage(logoUrl, 'PNG', 14, 12, 27, 27); } catch (e) {}
      doc.setFontSize(16).setFont('helvetica', 'bold');
      doc.text(`Sistema de Reservas AIP - ${currentYear}`, pageWidth / 2, 20, { align: 'center' });
      doc.setFontSize(14).setFont('helvetica', 'normal');
      doc.text(`${subtitle} - Nivel ${level}`, pageWidth / 2, 28, { align: 'center' });

      const sortedLevelReservations = levelReservations
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      const tableData = sortedLevelReservations
        .map((r, idx) => [
          idx + 1,
          format(new Date(r.date + 'T00:00:00'), 'dd/MM/yyyy'),
          r.shift === 'mañana' ? 'Mañana' : 'Tarde',
          r.time_slot || '—',
          r.user_name || '—',
          r.nivel || '—',
          r.grado || '—',
          r.seccion || '—',
          r.area || '—',
          r.actividad || '—',
          r.competencia || '—',
          r.recursos_digitales || '—',
          (Array.isArray(r.recursos_materiales) ? r.recursos_materiales.join(', ') : r.recursos_materiales) || '—',
          getAsistenciaLabel(r),
        ]);

      autoTable(doc, {
        head: [['#', 'Fecha', 'Turno', 'Horario', 'Docente', 'Nivel', 'Grado', 'Sección', 'Área', 'Actividad', 'Competencia', 'Rec. Digitales', 'Rec. Materiales', 'Asistencia']],
        body: tableData,
        startY: 40,
        theme: 'grid',
        styles: { fontSize: 7.5, cellPadding: 2 },
        // 📐 Anchos fijos (suman ~267 mm, el ancho útil de un A4 horizontal).
        // Antes las columnas se auto-dimensionaban según el texto de cada
        // nivel, por eso "Competencia" se veía distinta en Primaria que en
        // Inicial y Secundaria. Con anchos fijos las 3 tablas quedan iguales
        // y el texto largo de Competencia se ajusta en varias líneas.
        columnStyles: {
          0:  { cellWidth: 7,  halign: 'center' },  // #
          1:  { cellWidth: 18 },                    // Fecha
          2:  { cellWidth: 13 },                    // Turno
          3:  { cellWidth: 22 },                    // Horario
          4:  { cellWidth: 28 },                    // Docente
          5:  { cellWidth: 14 },                    // Nivel
          6:  { cellWidth: 11 },                    // Grado
          7:  { cellWidth: 12 },                    // Sección
          8:  { cellWidth: 21 },                    // Área
          9:  { cellWidth: 28 },                    // Actividad
          10: { cellWidth: 34 },                    // Competencia
          11: { cellWidth: 20 },                    // Rec. Digitales
          12: { cellWidth: 22 },                    // Rec. Materiales
          13: { cellWidth: 17 },                    // Asistencia
        },
        headStyles: { fillColor: [26, 77, 46], textColor: [255, 255, 255] },
        didParseCell: (data) => {
          if (data.section === 'body') {
            const rowRes = sortedLevelReservations[data.row.index];
            if (rowRes && rowRes.asistio === false) {
              data.cell.styles.textColor = [150, 150, 150];
              if (data.column.index === 13) {
                data.cell.styles.textColor = [200, 0, 0];
                data.cell.styles.fontStyle = 'bold';
              }
            }
          }
        },
        didDrawCell: (data) => {
          if (data.section === 'body') {
            const rowRes = sortedLevelReservations[data.row.index];
            // Tacha visualmente la fila (excepto la columna de Asistencia, para que se lea bien)
            if (rowRes && rowRes.asistio === false && data.column.index !== 13) {
              const { x, y, width, height } = data.cell;
              doc.setDrawColor(180, 0, 0);
              doc.setLineWidth(0.35);
              doc.line(x, y + height / 2, x + width, y + height / 2);
            }
          }
        },
      });

      addFooter(doc, pageWidth, dateTimeStr);
    });
  } else {
    // ✅ MODO USUARIO: Corregido para que NO salga en blanco
    try { doc.addImage(logoUrl, 'PNG', 14, 12, 30, 30); } catch (e) {}
    
    doc.setFontSize(16).setFont('helvetica', 'bold');
    doc.text(`Sistema de Reservas AIP - ${currentYear}`, pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(13).setFont('helvetica', 'normal');
    doc.text(subtitle, pageWidth / 2, 28, { align: 'center' });
    
    const fullName = `${userData.nombres || ''} ${userData.apellidos || ''}`.trim();
    doc.setFontSize(11);
    doc.text(`Docente: ${fullName}`, pageWidth / 2, 35, { align: 'center' });
    const nivelLabel = selectedLevel || userData.nivel;
    if (nivelLabel) doc.text(`Nivel: ${nivelLabel}`, pageWidth / 2, 41, { align: 'center' });

    const sortedReservations = reservations
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    const tableData = sortedReservations
      .map((r, idx) => [
        idx + 1,
        format(new Date(r.date + 'T00:00:00'), 'dd/MM/yyyy'),
        r.shift === 'mañana' ? 'Mañana' : 'Tarde',
        r.time_slot || '—',
        r.grado || '—',
        r.seccion || '—',
        r.area || '—',
        r.actividad || '—',
        r.competencia || '—',
        r.recursos_digitales || '—',
        (Array.isArray(r.recursos_materiales) ? r.recursos_materiales.join(', ') : r.recursos_materiales) || '—',
        getAsistenciaLabel(r),
      ]);

    autoTable(doc, {
      head: [['#', 'Fecha', 'Turno', 'Horario', 'Grado', 'Sección', 'Área', 'Actividad', 'Competencia', 'Rec. Digitales', 'Rec. Materiales', 'Asistencia']],
      body: tableData,
      startY: (selectedLevel || userData.nivel) ? 48 : 42,
      theme: 'grid',
      styles: { fontSize: 7.5, cellPadding: 3 },
      headStyles: { fillColor: [26, 77, 46], textColor: [255, 255, 255] },
      didParseCell: (data) => {
        if (data.section === 'body') {
          const rowRes = sortedReservations[data.row.index];
          if (rowRes && rowRes.asistio === false) {
            data.cell.styles.textColor = [150, 150, 150];
            if (data.column.index === 11) {
              data.cell.styles.textColor = [200, 0, 0];
              data.cell.styles.fontStyle = 'bold';
            }
          }
        }
      },
      didDrawCell: (data) => {
        if (data.section === 'body') {
          const rowRes = sortedReservations[data.row.index];
          if (rowRes && rowRes.asistio === false && data.column.index !== 11) {
            const { x, y, width, height } = data.cell;
            doc.setDrawColor(180, 0, 0);
            doc.setLineWidth(0.35);
            doc.line(x, y + height / 2, x + width, y + height / 2);
          }
        }
      },
    });

    addFooter(doc, pageWidth, dateTimeStr);
  }

  const levelSuffix = selectedLevel ? `_${String(selectedLevel).trim()}` : '';
  doc.save(`Reporte${levelSuffix}_${new Date().toISOString().slice(0, 10)}.pdf`);
};

// Función auxiliar para evitar repetir código de pie de página y firma
const addFooter = (doc, pageWidth, dateTimeStr) => {
  const finalY = doc.lastAutoTable.finalY + 40;
  doc.setFontSize(12).setFont('helvetica', 'bold');
  doc.text('Firma del PIP', pageWidth / 2, finalY, { align: 'center' });
  
  const h = doc.internal.pageSize.getHeight();
  doc.setFontSize(8).setFont('helvetica', 'normal').setTextColor(100);
  doc.text(dateTimeStr, pageWidth - 15, h - 10, { align: 'right' });
};

// ✅ NUEVA FUNCIÓN: Específica para los cuadros de distribución del Admin
// ✅ FUNCIÓN ACTUALIZADA: Cuadro de distribución semanal con logo y pie de página
export const generateAdminTablePDF = (tableData, timeSlots, days, shiftName, dates) => {
  const doc = new jsPDF('l', 'mm', 'a4'); // Orientación horizontal
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const logoUrl = '/logo_aip.png';
  const dateTimeStr = `Generado el: ${getFormattedDateTime()}`;
  const currentYear = getCurrentYear();

  // ✅ 1. LOGO en el encabezado (esquina superior izquierda)
  try {
    doc.addImage(logoUrl, 'PNG', 15, 10, 30, 30);
  } catch (e) {
    console.warn('Logo no cargado en PDF de distribución semanal');
  }

  // ✅ 2. Título principal con año
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(`Distribución Semanal - Turno ${shiftName} ${currentYear}`, pageWidth / 2, 20, { align: 'center' });
  
  // ✅ 3. Rango de fechas
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  const dateRange = `Semana del ${format(dates[0], 'dd/MM/yyyy')} al ${format(dates[5], 'dd/MM/yyyy')}`;
  doc.text(dateRange, pageWidth / 2, 28, { align: 'center' });

  // ✅ 4. Preparar Cabeceras (Hora + Horario + Días con fecha)
  const headers = [
    'Hora',
    'Horario',
    ...days.map((day, i) => `${day.toUpperCase()} ${format(dates[i], 'dd/MM')}`)
  ];

  // ✅ 5. Preparar Filas
  const rows = timeSlots.map((slot, idx) => {
    const row = [`${idx + 1}H`, slot]; // 1ra columna: número de hora, 2da: horario
    days.forEach(day => {
      const cellValue = tableData[idx][day];
      row.push(cellValue === '-' ? '' : cellValue);
    });
    return row;
  });

  // ✅ 6. Generar tabla
  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 38,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 2.5, halign: 'center', valign: 'middle' },
    headStyles: { 
      fillColor: shiftName === 'Mañana' ? [26, 77, 46] : [184, 84, 22], // Verde oscuro / Ámbar
      textColor: [255, 255, 255] 
    },
    columnStyles: {
      0: { fontStyle: 'bold', fillColor: [245, 245, 245], cellWidth: 14 },
      1: { fontStyle: 'bold', fillColor: [245, 245, 245], cellWidth: 32 }
    }
  });

  // ✅ 7. PIE DE PÁGINA con fecha/hora (centrado inferior)
  
  // Fecha/hora de generación (derecha inferior)
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100);
  doc.text(dateTimeStr, pageWidth - 15, pageHeight - 10, { align: 'right' });

  // ✅ 8. Guardar PDF
  doc.save(`Cuadro_Semanal_${shiftName}_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
};

// ✅ PDF de semana actual (solo para usuarios)
// timeSlotsByShift viene del backend (fuente única de verdad, ver server.py /api/system/time-slots)
export const generateCurrentWeekPDF = (reservations, userData, timeSlotsByShift = {}) => {
  if (!reservations || reservations.length === 0) return null;

  const now = new Date();
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const weekdays = Array.from({ length: 6 }, (_, i) => addDays(weekStart, i));
  const currentYear = getCurrentYear();

  const morningSlots = timeSlotsByShift['mañana'] || [];
  const afternoonSlots = timeSlotsByShift['tarde'] || [];

  const userReservationsMap = {};
  reservations.forEach(r => {
    if (!userReservationsMap[r.date]) {
      userReservationsMap[r.date] = {};
    }
    userReservationsMap[r.date][r.time_slot] = true;
  });

  const hasMorningReservation = reservations.some(r => 
    r.shift === 'mañana' && 
    weekdays.some(day => format(day, 'yyyy-MM-dd') === r.date)
  );

  const hasAfternoonReservation = reservations.some(r => 
    r.shift === 'tarde' && 
    weekdays.some(day => format(day, 'yyyy-MM-dd') === r.date)
  );

  if (!hasMorningReservation && !hasAfternoonReservation) return null;

  const doc = new jsPDF('l', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const logoUrl = '/logo_aip.png';

  try {
    doc.addImage(logoUrl, 'PNG', 14, 12, 30, 30);
  } catch (e) {
    console.warn('Logo no cargado en PDF de semana');
  }

  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(`Mis Reservas - Semana Actual ${currentYear}`, pageWidth / 2, 20, { align: 'center' });
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(
    `Docente: ${userData?.nombres || ''} ${userData?.apellidos || ''}`,
    pageWidth / 2,
    28,
    { align: 'center' }
  );
  doc.text(
    `Semana del ${format(weekStart, 'dd MMMM yyyy', { locale: es })} al ${format(addDays(weekStart, 5), 'dd MMMM yyyy', { locale: es })}`,
    pageWidth / 2,
    36,
    { align: 'center' }
  );

  let currentY = 46;
  const getOccupant = (dateStr, slot) => {
    return userReservationsMap[dateStr]?.[slot] ? (userData?.nombres || 'Tú') : '-';
  };

  if (hasMorningReservation) {
    const morningHead = [['Hora', 'Turno Mañana', ...weekdays.map(day => format(day, 'EEE dd MMM', { locale: es }))]];
    const morningBody = morningSlots.map((slot, idx) => [
      `${idx + 1}H`,
      slot,
      ...weekdays.map(day => getOccupant(format(day, 'yyyy-MM-dd'), slot))
    ]);
    autoTable(doc, {
      head: morningHead,
      body: morningBody,
      startY: currentY,
      theme: 'grid',
      styles: { fontSize: 10, cellPadding: 4, halign: 'center' },
      headStyles: { fillColor: [26, 77, 46], textColor: [245, 230, 211] },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 14 } },
    });
    currentY = doc.lastAutoTable.finalY + 10;
  }

  if (hasAfternoonReservation) {
    const afternoonHead = [['Hora', 'Turno Tarde', ...weekdays.map(day => format(day, 'EEE dd MMM', { locale: es }))]];
    const afternoonBody = afternoonSlots.map((slot, idx) => [
      `${idx + 1}H`,
      slot,
      ...weekdays.map(day => getOccupant(format(day, 'yyyy-MM-dd'), slot))
    ]);
    autoTable(doc, {
      head: afternoonHead,
      body: afternoonBody,
      startY: currentY,
      theme: 'grid',
      styles: { fontSize: 10, cellPadding: 4, halign: 'center' },
      headStyles: { fillColor: [26, 77, 46], textColor: [245, 230, 211] },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 14 } },
    });
    currentY = doc.lastAutoTable.finalY + 10;
  }

  // ✅ Detalle de cada reserva de la semana: Grado, Sección, Área y Actividad
  const weekReservations = reservations
    .filter(r => weekdays.some(day => format(day, 'yyyy-MM-dd') === r.date))
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  if (weekReservations.length > 0) {
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Detalle de Reservas', 14, currentY + 8);

    const detailHead = [['Fecha', 'Turno', 'Horario', 'Grado', 'Sección', 'Área', 'Actividad', 'Competencia', 'Rec. Digitales', 'Rec. Materiales', 'Asistencia']];
    const detailBody = weekReservations.map(r => [
      format(new Date(r.date + 'T00:00:00'), 'dd/MM/yyyy'),
      r.shift === 'mañana' ? 'Mañana' : 'Tarde',
      r.time_slot || '—',
      r.grado || '—',
      r.seccion || '—',
      r.area || '—',
      r.actividad || '—',
      r.competencia || '—',
      r.recursos_digitales || '—',
      (Array.isArray(r.recursos_materiales) ? r.recursos_materiales.join(', ') : r.recursos_materiales) || '—',
      getAsistenciaLabel(r),
    ]);

    autoTable(doc, {
      head: detailHead,
      body: detailBody,
      startY: currentY + 14,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [26, 77, 46], textColor: [245, 230, 211] },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      didParseCell: (data) => {
        if (data.section === 'body') {
          const rowRes = weekReservations[data.row.index];
          if (rowRes && rowRes.asistio === false) {
            data.cell.styles.textColor = [150, 150, 150];
            if (data.column.index === 10) {
              data.cell.styles.textColor = [200, 0, 0];
              data.cell.styles.fontStyle = 'bold';
            }
          }
        }
      },
      didDrawCell: (data) => {
        if (data.section === 'body') {
          const rowRes = weekReservations[data.row.index];
          if (rowRes && rowRes.asistio === false && data.column.index !== 10) {
            const { x, y, width, height } = data.cell;
            doc.setDrawColor(180, 0, 0);
            doc.setLineWidth(0.35);
            doc.line(x, y + height / 2, x + width, y + height / 2);
          }
        }
      },
    });
    currentY = doc.lastAutoTable.finalY + 30;
  } else {
    currentY += 30;
  }

  const dateTimeStr2 = `Generado el: ${getFormattedDateTime()}`;
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const w = doc.internal.pageSize.getWidth();
    const h = doc.internal.pageSize.getHeight();
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(dateTimeStr2, w - 15, h - 10, { align: 'right' });
  }
  return doc;
};